import moment from "moment";
import { DEFAULT_EXTRA_PAYMENTS, OvertimeData, OvertimeType, MANUAL_EXTRA_PAYMENT_TYPES } from "./constants";

// Resolves the display label for a payrollDetails-response payrollWageType entry:
// {label, value} (records synced after wage-type snapshotting was added) -> label directly.
// {value} only (older record) -> reverse-lookup value against the wage-type catalog, else show the raw value.
// null/undefined (never synced) -> '-'.
export const resolvePayrollWageTypeLabel = (payrollWageType, commonStore) => {
    return payrollWageType?.label
        ?? commonStore.getAllWageTypesCombined().find(w => w.value === payrollWageType?.value)?.label
        ?? payrollWageType?.value
        ?? '-';
}

// Resolves a raw wage-type code (e.g. the hardcoded Visma/Xledger default eventcodes/dbIds shown in
// admin-UI "system default" notes) to its human-readable catalog label. Falls back to the raw code
// if it isn't found in the current wage-type catalog (e.g. catalog not yet fetched, or code removed).
export const resolveWageTypeCodeLabel = (code, commonStore) => {
    if (!code) return null;
    return commonStore.getAllWageTypesCombined().find(w => String(w.value) === String(code))?.label ?? code;
}

export const _dayMap = { 0: "6", 1: "0", 2: "1", 3: "2", 4: "3", 5: "4", 6: "5" };
export const overtimeModes = {
    companyShift: "COMPANYSHIFT",
    automatic: "AUTOMATIC",
    manual: "MANUAL"
}

export const currentYearCycle = (dateTimeFormat, timezoneHours) => {
    const today = moment(new Date()).add(timezoneHours, 'hours').format('YYYY-MM-DD');
    return `${moment(today).startOf('year').format(dateTimeFormat)} - ${moment(today).endOf('year').format(dateTimeFormat)}`;
}

/**
    * @name getPercentageFromNumberValues
    * @description returns array of percentages based on passed aaray of values
    * @param {Object} numArray
    * 
    * @returns {Object} percentageArray
*/
export const getPercentageFromNumberValues = (numArray) => {
    if (!numArray || !numArray.length) return [];
    const percentageArray = [];
    const initialValue = 0;
    const total = numArray.reduce((accumulator, currentValue) =>
        parseInt(accumulator) + parseInt(currentValue), initialValue);
    numArray.forEach(num => {
        percentageArray.push(Math.round((num / total) * 100));
    })
    return percentageArray;
}

/**
    * @name getPercentage
    * @description returns the percentage for the current quatity out of total quantity
    * @param {Number} total_qty
    * @param {Number} current_qty
    * 
    * @returns {Number} percentage
*/
export const getPercentage = (total_qty, current_qty) => {
    if (!total_qty) return 0;
    return Math.round((current_qty / total_qty) * 100)
}


/**
    * @name getFormattedAddress
    * @description returns address string according to regional settings
    * @param {Object} address
    * @param {Object} dateTimeRules
    * 
    * @returns {String} address_string
*/
export const getFormattedAddress = (address, address_format) => {
    address.post_number = address.post_number != null ? address.post_number : false;
    address.post_place = address.post_place != null && address.post_place.trim() !== "" ? address.post_place : false;
    address.address = address.address != null && address.address.trim() !== "" ? address.address : false;
    let formated_address = "";
    if (address_format === 'address,pincode,city') {
        formated_address = `${address.address ? address.address : ""}, ${address.post_number ? address.post_number : ""} ${address.post_place ? address.post_place : ""}`
    }
    else {
        formated_address = `${address.address ? address.address : ""}, ${address.post_place ? address.post_place : ""} ${address.post_number ? address.post_number : ""}`
    }
    return formated_address;
}


/**     
    * @name formatBankAccountNumber
    * @description Formats a bank account number by removing non-digit characters and applying a desired format.
    *
    * @param {string} accountNumber - The bank account number to be formatted.
    * @return {string} formattedNumber - The formatted bank account number.
 */
export const formatBankAccountNumber = (accountNumber) => {
    const cleanedNumber = accountNumber.replace(/\D/g, '');
    const formattedNumber = cleanedNumber.replace(/(\d{4})(\d{2})(\d{1,5})(\d*)/, '$1-$2-$3$4');
    return formattedNumber;
}

/**
    * @name compareObjects
    * @description method used for sorting array of objects using the target property
    * @param {Object} a
    * @param {Object} b
    * @param {String} targetProperty
    * @param {Boolean} desc
    * 
    * @returns {Integer} difference
*/
export const compareObjects = (a, b, targetProperty, desc = false) => {
    if (!targetProperty) return 0;
    const targetPropertyDataType = typeof a[targetProperty];
    const _a = a[targetProperty] ? a[targetProperty] : targetPropertyDataType === 'number' ? 0 : '';
    const _b = b[targetProperty] ? b[targetProperty] : targetPropertyDataType === 'number' ? 0 : '';
    if (targetPropertyDataType === 'number') {
        return desc ? _b - _a : _a - _b;
    }
    else {
        return desc ? _b.localeCompare(_a) : _a.localeCompare(_b);
    }
}


/**
    * @name isWeekend
    * @description method used for checking if day is weekend
    * @param {Integer} day
    * @param {String} clientDefultHolidays
    * 
    * @returns {Boolean} isWeekend
*/
export const isWeekend = (day, clientDefultHolidays) => {
    if (!clientDefultHolidays) return false;
    if (day === 0) {
        day = 6;
    } else {
        day -= 1;
    }
    return clientDefultHolidays.indexOf(day.toString()) > -1;
}


/**
    * @name checkIfTimelogHasWeekendInExtrapayments
    * @description method used for checking if day is weekend as per rules extrapayment rules
    * @param {string} forDate
    * @param {string} employeeExtraPayments
    * @param {Object} extraPaymentRules
    * 
    * @returns {Boolean} isWeekend
*/
export const checkIfCurrentDaysIsWeekendAsPerExtraPaymentSettings = (forDate, employeeExtraPayments = "", extraPaymentRules = []) => {
    if (!forDate || !moment(forDate).isValid()) return false;
    const weekendRule = extraPaymentRules.find(ele => ele.code === 'weekends' && ele.isActive);
    if (!weekendRule) return false;
    if (!employeeExtraPayments || !employeeExtraPayments.includes('weekends')) return false;
    const _forDateDay = _dayMap[moment(forDate).day()];
    return weekendRule.daysOfWeek.includes(_forDateDay);
}


/**
    * @name calculateBusinessDays
    * @description method used for calculate the business days
    * @param {string} from
    * @param {string} to
    * @param {string} defaultHolidaysForVacation
    * @param {boolean} accountForHolidays
    * 
    * @returns {Integer} workingDays
*/
export const calculateBusinessDays = (from, to, defaultHolidaysForVacation, accountForHolidays, commonStore = null, entityType = null) => {
    const d1 = moment(from);
    const d2 = moment(to);
    const days = d2.diff(d1, "days") + 1;
    let workingDays = 0;

    for (let i = 0; i < days; i++) {
        const currentDay = moment(d1).add(i, "days");
        const _forDateDay = _dayMap[currentDay.day()];
        if (accountForHolidays && defaultHolidaysForVacation?.includes(_forDateDay)) continue;

        if (commonStore && entityType) {
            const holidayInfo = commonStore.getHolidayInfo(currentDay);
            if (holidayInfo.isHoliday && holidayInfo.details) {
                const holiday = holidayInfo.details;
                if ((entityType === 'absence' && holiday.accountedInAbsence) || (entityType === 'vacation' && holiday.accountedInVacation)) continue;
            }
        }
        workingDays++;
    }
    return workingDays;
}

/**
    * @name getDaysFromDaysIndex
    * @description method used get Days from Days Index
    * @param {String} clientDefultHolidays
    * 
    * @returns {String} Days String
*/
export const getDaysFromDaysIndex = (clientDefultHolidays) => {
    const arr = clientDefultHolidays.split(',')
    getMomemntLocale();
    const _arr = arr.map(ele => {
        if (ele === 6)
            return 0;
        else
            return ele = parseInt(ele) + 1;
    })
    const days = _arr.map(ele => moment().day(ele).format('ddd'))
    return days.join(',')
}

/**
    * @name getMomemntLocale
    * @description method used get moment locale
    * 
    * @returns {Object} get moment locale
*/
export const getMomemntLocale = () => {
    const lang = window.sessionStorage.getItem('locale');
    return moment.locale(lang === 'no' ? 'nb' : lang);
}

export const compareFuncInt = (a, b) => a - b;

/**
    * @name isExtraPaymentMatchedToTask
    * @description true when a rule applies to the given task — either a single-task rule (ele.task)
    * or a multi-task rule whose inline taskIds contains the task.
    * @param {Object} ele
    * @param {number} task_id
    *
    * @returns {boolean}
*/
export const isExtraPaymentMatchedToTask = (ele, task_id) => {
    if (!task_id) return false;
    if (ele.isMultiTask)
        return Array.isArray(ele.taskIds) && ele.taskIds.some(id => parseInt(id) === parseInt(task_id));
    return ele.task && parseInt(ele.task) === parseInt(task_id);
};

/**
    * @name getUserExtraPayments
    * @description method to get all the assigned extra payments in user profile
    * from control panel
    * @param {Object} user
    * @param {Object} extraPaymentRules
    * @param {Object} forDate
    * @param {Object} timelogShift
    * @param {boolean} employeeAssignedForEmergencyShift
    * @param {number} task_id
    *
    * @returns {Object} extraPaymentS
*/
export const getUserExtraPayments = (user, extraPaymentRules, forDate, timelogShift, employeeAssignedForEmergencyShift, task_id, commonStore) => {
    if (!user || !extraPaymentRules || !forDate) return [];
    const holidayInfo = commonStore && commonStore.getHolidayInfo(forDate);
    const isHolidayWithExtraPayment = holidayInfo?.isHoliday && holidayInfo?.details?.additionalPayment;
    extraPaymentRules = extraPaymentRules.filter(ele =>
        ele.code === "emergency_shifts" || !DEFAULT_EXTRA_PAYMENTS.includes(ele.code)).map(ele => {
            return {
                ...ele,
                // isEditable: ele.fullDay && ele.code !== 'weekends' && (!isHolidayWithExtraPayment && !ele.fullDay),
                isEditable: false,
                helpText: 'These will be automatically filled by the system',
                // helpText: !ele.fullDay || ele.code === 'weekends' || (isHolidayWithExtraPayment && ele.fullDay) ? 'These will be automatically filled by the system' : 'Please fill holiday hours manually, if you are working on holiday',
                hasConfigIssue: true,
                configIssueText: ""
            }
        });

    const _extraPaymentsInUserProfile = user.extra_payments?.split(',') || [];
    _extraPaymentsInUserProfile.push('emergency_shifts');
    const _forDateDay = _dayMap[moment(forDate).day()];
    let __extraPaymentRules = [];

    if (timelogShift && timelogShift.user_group > 0) {
        __extraPaymentRules = extraPaymentRules && extraPaymentRules.length ?
            extraPaymentRules.filter(ele => ele.isActive
                && ele.daysOfWeek.includes(_forDateDay)
                && (ele.userGroups === 'all' || ele.userGroups?.split(',').includes(`${timelogShift.user_group}`))
                && (ele.employmentTypes === 'all' || ele.employmentTypes === user.payment_mode)
                && !ele.task
            ).map(ele => {
                return { ...ele, hasConfigIssue: false, configIssueText: "" };
            })
            : [];
    }

    if (_extraPaymentsInUserProfile.length && extraPaymentRules) {
        extraPaymentRules.forEach(ele => {
            // task-matching rules (single- or multi-task) are handled exclusively by the taskExtraPayment block below;
            if (isExtraPaymentMatchedToTask(ele, task_id)) return;
            if (ele.isActive
                && _extraPaymentsInUserProfile.includes(ele.code)
                && ele.daysOfWeek.includes(_forDateDay)
                && (ele.employmentTypes === 'all' || ele.employmentTypes === user.payment_mode)
                && !__extraPaymentRules.some(_ele => _ele.code === ele.code)
                && !ele.task && (!ele.fullDay || (ele.fullDay && ele.code !== 'weekends' && isHolidayWithExtraPayment))) {
                if (ele.code === 'emergency_shifts' && !employeeAssignedForEmergencyShift)
                    __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "This Additional Payment is not assigned for the this employee" });
                else
                    __extraPaymentRules.push({ ...ele, hasConfigIssue: false, configIssueText: "" });
            } else {
                if (!ele.isActive) {
                    __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "This Additional Payment is not active in control panel" });
                }
                else if (!_extraPaymentsInUserProfile.includes(ele.code)) {
                    __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "This Additional Payment is not active in employee profile" });
                }
                else if (ele.task && ele.task !== task_id) {
                    __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "This Additional Payment is not available for this task" });
                }
                else if (ele.fullDay && ele.code !== 'weekends' && !isHolidayWithExtraPayment) {
                    __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "This Additional Payment is only available for holidays" });
                }
                else {
                    __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "The current timesheet is not matching all the Additional Payment settings" });
                }
            }
        });
    }
    if (__extraPaymentRules.some(ele => ele.code === 'weekends' && !ele.hasConfigIssue)) {
        __extraPaymentRules = __extraPaymentRules.map(ele => {
            if (ele.code !== 'weekends' && !ele.hasConfigIssue) {
                ele.isEditable = false;
                ele.hasConfigIssue = true;
                ele.configIssueText = "Timesheet will be considered under weekends extra payments";
            }
            return ele;
        })
    }
    const taskExtraPayment = extraPaymentRules.filter(ele => isExtraPaymentMatchedToTask(ele, task_id));
    if (taskExtraPayment?.length)
        taskExtraPayment.forEach(ele => {
            if (!ele.isActive)
                __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: true, configIssueText: "This Additional Payment is not active in control panel" })
            // Manual-entry types (hourly / points): let the user type the value for this task.
            else if (MANUAL_EXTRA_PAYMENT_TYPES.includes(ele.extraPaymentType?.value))
                __extraPaymentRules.push({ ...ele, isEditable: true, hasConfigIssue: false, configIssueText: "", helpText: "Enter additional payment hours manually for this task" })
            else
                __extraPaymentRules.push({ ...ele, isEditable: false, hasConfigIssue: false, configIssueText: "" })
        })

    __extraPaymentRules = __extraPaymentRules.sort((a, b) => (+b.isEditable) - (+a.isEditable));
    return __extraPaymentRules;
}

/**
    * @name getGroupName
    * @description method used get all members with groups
    * 
    * @returns {Map} get Members
*/
export const getGroupName = (clientGroups) => {
    const getClientGroupMap = new Map()
    clientGroups.forEach(ele => {
        getClientGroupMap.set(ele.code, ele)
    })
    return getClientGroupMap
}

/**
    * @name translateLabels
    * @description method used translate labels inside options array.
    * @param {Array} options
    * @param {Function} translate
    * @param {string} propName
    *
    * @returns {Array} translated options
*/
export const translateLabels = (options, translate, propName = null) => {
    options.forEach(ele => {
        ele.title = translate(ele.key);
        if (propName) ele[propName] = translate(ele.key)
    })
}

/**
    * @name getDateAsPerServer
    * @description method to get the date with server timezone difference
    * @param {Number} timezoneGMT
    * @param {Date} forDate
    * 
    * @returns {import("moment").Moment} 
*/
export const getDateAsPerServer = (timezoneGMT, forDate = new Date()) => {
    return moment(forDate).add(timezoneGMT, 'hours')
}

/**
    * @name getDistanceZone
    * @description method used for checks and returns distance object.
    * @param {Number} logDistance
    * @param {Object} clientKilometersRules
    * 
    * @returns {Object} distanceObject
*/
export const getDistanceZone = (logDistance, clientKilometersRules) => {
    const kilometersSort = clientKilometersRules.map(ele => {
        ele.max_km = parseFloat(ele.max_km)
        return ele
    }).sort((a, b) => a.max_km - b.max_km);
    for (let i = 0; i < kilometersSort.length; i++) {
        if (kilometersSort[i].max_km >= logDistance) return kilometersSort[i];
    }
    return kilometersSort[kilometersSort.length - 1];
}

/**
    * @name getCompanyShiftDetails
    * @description method used for getting the company shift
    * @param {Object} clientAndConfigs
    * @param {Number} companyShiftId
    * @param {Function} minutesToTimeInput
    * 
    * @returns {Object} currentCompanyShift
*/
export const getCompanyShiftDetails = (clientAndConfigs, companyShiftId, minutesToTimeInput) => {
    let currentShift = {
        start: "",
        end: "",
        total: "",
        break: 30,
        companyShiftId: null,
        maximumWorkLimitInDay: 9,
        maximumWorkLimitInWeek: 40,
        overtimeThreshold: 15,
        overtimes: [],
        period: "day",
    }
    if (clientAndConfigs.client && clientAndConfigs.client.allow_company_shifts && clientAndConfigs.companyShifts && clientAndConfigs.companyShifts.length) {
        let companyShift = clientAndConfigs.companyShifts.find(e => e.id === companyShiftId);
        if (!companyShift)
            companyShift = clientAndConfigs.companyShifts[0];
        currentShift.start = minutesToTimeInput(companyShift.start_time);
        currentShift.end = minutesToTimeInput(companyShift.end_time);
        currentShift.total = minutesToTimeInput(companyShift.normal_work_day);
        currentShift.break = companyShift.break_time_minutes;
        currentShift.companyShiftId = companyShift.id;
        currentShift.maximumWorkLimitInDay = companyShift.maximum_work_limit_in_day;
        currentShift.overtimeThreshold = companyShift.overtime_threshold;
        currentShift.overtimes = companyShift.Company_Shift_Overtimes;
    } else {
        currentShift.start = clientAndConfigs?.client?.data?.basicRules?.startTimeRules?.start || '08:00';
        currentShift.end = clientAndConfigs?.client?.data?.basicRules?.startTimeRules?.end || '16:00';
        currentShift.total = clientAndConfigs?.client?.data?.basicRules?.startTimeRules?.total;
        currentShift.break = clientAndConfigs?.client?.data?.basicRules?.breakTimeRules.minutes || 0;
    }
    return currentShift;
}

/**
    * @name getOvertimeSettings
    * @description method used for getting the company overtime settings
    * @param {Object} clientAndConfigs
    * @param {Object} companyShift
    * 
    * @returns {Object} currentOvertime
*/
export const getOvertimeSettings = (clientAndConfigs, companyShift) => {
    let automaticOvertime = {
        calc_percent: 0,
        calc_period: 'none',
        max_hours: 0,
        max_minutes: 0,
        integrated_wageType: null,
        overtimeThresoldMinutes: 15
    }
    if (clientAndConfigs.client.allow_company_shifts && companyShift.overtimes && companyShift.overtimes.length) {
        automaticOvertime.calc_percent = companyShift.overtimes[0].Overtime_Setting.overtime;
        automaticOvertime.calc_period = companyShift.period;
        automaticOvertime.max_hours = 0;
        automaticOvertime.max_minutes = companyShift.maximumWorkLimitInDay;
        automaticOvertime.overtimeThresoldMinutes = companyShift.overtimeThreshold;
        automaticOvertime.integrated_wageType = companyShift.overtimes[0].Overtime_Setting.integrated_wagetype;

    }
    else if (clientAndConfigs?.client?.data?.basicRules?.overtimeAutomaticMode) {
        automaticOvertime.calc_percent = Number(clientAndConfigs?.client?.data?.basicRules?.overtimeCalcRules?.calc_percent || 0);
        automaticOvertime.calc_period = clientAndConfigs?.client?.data?.basicRules?.overtimeCalcRules?.calc_period;
        automaticOvertime.max_hours = Number(clientAndConfigs?.client?.data?.basicRules?.overtimeCalcRules?.max_hours || 0);
        automaticOvertime.max_minutes = Number(clientAndConfigs?.client?.data?.basicRules?.overtimeCalcRules?.max_minutes || 0);
        automaticOvertime.overtimeThresoldMinutes = Number(clientAndConfigs?.client?.data?.basicRules?.overtimeThresoldMinutes || 15);
        automaticOvertime.integrated_wageType = clientAndConfigs?.client?.data?.basicRules?.overtimeCalcRules?.integrated_wageType;
    }
    else {
        automaticOvertime.calc_percent = 50;
        automaticOvertime.calc_period = 'day';
        automaticOvertime.max_hours = 9;
        automaticOvertime.max_minutes = 0;
        automaticOvertime.overtimeThresoldMinutes = 0;
        automaticOvertime.integrated_wageType = null;
    }
    return automaticOvertime;
}

/**
    * @name getOvertimeMode
    * @description method used for getting the company overtime mode
    * @param {Object} clientAndConfigs
    * 
    * @returns {string} currentOvertimeMode
*/
export const getOvertimeMode = (clientAndConfigs) => {
    if (clientAndConfigs?.client?.allow_company_shifts)
        return overtimeModes.companyShift;
    else if (clientAndConfigs?.client?.data?.basicRules?.overtimeAutomaticMode)
        return overtimeModes.automatic;
    else
        return overtimeModes.manual
}


/**
    * @name validateStartAndEndTime
    * @description method used for validating start and end time
    * @param {number} startTime 
    * @param {number} endTime
    * 
    * @returns {Object} 
*/
export const validateStartAndEndTime = (startTime, endTime) => {
    if (isNaN(startTime)) {
        return { hasError: true, error: "Please enter valid start time" };
    }
    if (isNaN(endTime)) {
        return { hasError: true, error: "Please enter valid end time" };
    }
    if (Number(startTime) === Number(endTime)) {
        return { hasError: true, error: "Start time and End time cannot be the same" };
    }
    return { hasError: false };
};

/**
    * @name validateRequestPayload
    * @description method used for validating the request payloads
    * @param {Object} reqBody 
    * @param {Array} requiredFields
    * @param {Array} validationMessages
    * @param {Function} translate
    * 
    * @returns {Object} 
*/
export const validateRequestPayload = (reqBody, requiredFields, validationMessages, translate) => {
    let ruleValidation = (value) => value && (typeof value === 'string' || typeof value === 'number') && String(value).trim().length > 0
    const rules = {};
    requiredFields.forEach((field, index) => {
        rules[field] = (value) => {
            return (ruleValidation(value) ? true : translate(validationMessages[index]) + " " + translate('is required'))
        };
    });

    const errors = [];
    Object.keys(rules).forEach((field) => {
        const result = rules[field](reqBody[field]);
        if (result !== true) {
            errors.push({ ["error"]: result });
            return
        }
    });
    return errors.length ? errors : null;
};


/**
    * @name renderCompanyShiftDetails
    * @description method used for rendering company shift details
    * @param {Object} companyShift 
    * @param {function} t
    * @param {function} minutesToTimeInput
    * 
    * @returns {Object} renderbody
*/
export const renderCompanyShiftDetails = (companyShift, t, minutesToTimeInput, timeFormat, dateTimeRules) => {
    return (
        <>
            <div><strong>{t("Company Shift Details")}</strong></div>
            <div>{t('Time')}: <strong>{timeFormat(companyShift.start_time, dateTimeRules)} {t("to")} {timeFormat(companyShift.end_time, dateTimeRules)}</strong></div>
            <div>{t('Break')}: <strong>{companyShift.break_time_minutes || 0}&nbsp;{t('Minutes')}</strong>&nbsp;&nbsp;{t('Total')}: <strong>{minutesToTimeInput(companyShift.normal_work_day || 0)}</strong> {t('hours')}</div>
            <div>{t('Buffer time')}: <strong>{companyShift.buffer_time} {t('Minutes')}</strong></div>
            {companyShift?.Company_Shift_Overtimes?.length > 0 && (
                <div>
                    <div className="fw-bold mt-1">{t('Overtime Settings')}:</div>
                    <div>{t('Maximum Works hours in day (Excluding Break)')}: <strong>{minutesToTimeInput(companyShift.maximum_work_limit_in_day) || 0} {t('hours')}</strong></div>
                    <div>{t('Overtime threshold')}: <strong>{companyShift.overtime_threshold || 0} {t('Minutes')}</strong></div>
                    {companyShift?.Company_Shift_Overtimes.map((overtime, index) => (
                        <div key={index}>
                            {overtime.Overtime_Setting?.name || `${t('Overtime')} ${index + 1}`}: <strong>{timeFormat(overtime.from, dateTimeRules)} - {timeFormat(overtime.to, dateTimeRules)}</strong>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}

/**
    * @name getCompanyOvertimeTypes
    * @description method to get the company overtime types
    * @param {Object} client
    * @param {Array} companyOvertimes
    * @param {Boolean} passPlainCodes for passing the plain overtime code rather than id
    * 
    * @returns {Object} overtimeTypeHashMap
*/
export const getCompanyOvertimeTypes = (client, overtimes, passPlainCodes = false) => {
    let overtimeTypeHashMap = {};
    if (client.allow_company_shifts && overtimes && overtimes.length) {
        overtimes.forEach(e => {
            overtimeTypeHashMap[e.id] = {
                code: passPlainCodes ? e.overtime : e.id,
                name: e.name,
                value: e.overtime,
                payrollWageType: e.integrated_wagetype
            }
        })
    } else {
        if (client.data && client.data.basicRules && !client.data.basicRules.overtimeAutomaticMode
            && client.data.basicRules.overtime_types && client.data.basicRules.overtime_types.length) {
            client.data.basicRules.overtime_types.forEach(e => {
                overtimeTypeHashMap[e.code] = {
                    code: e.code,
                    name: e.name,
                    value: parseFloat(e.multiplier),
                    payrollWageType: e.payrollWageType
                }
            })
        }
        else if (client.data && client.data.basicRules && client.data.basicRules.overtimeAutomaticMode
            && client.data.basicRules.overtimeCalcRules && client.data.basicRules.overtimeCalcRules.calc_percent) {
            const multiplier = client.data.basicRules.overtimeCalcRules.calc_percent;
            if (parseFloat(multiplier) > 0) {
                const code = `automatic_${multiplier}`;
                overtimeTypeHashMap[code] = {
                    code: code,
                    name: `Overtime ${multiplier}%`,
                    value: parseFloat(multiplier),
                    payrollWageType: client.data.basicRules.overtimeCalcRules.integrated_wageType
                }
            }
        }
    }
    return overtimeTypeHashMap;
}


/**
    * @name getWeekNumbersBetweenDates
    * @description method to get the week numbers between dates
    * @param {moment} from_date
    * @param {moment} end_date
    * 
    * @returns {Array} weekNumberArray
*/
export const getWeekNumbersBetweenDates = (from_date, end_date) => {
    const weekNumberArray = [];
    let currentDate = from_date.clone();

    while (currentDate.isSameOrBefore(end_date)) {
        const weekNumber = currentDate.isoWeek();
        if (weekNumber < weekNumberArray.length) {
            break;
        }
        weekNumberArray.push(weekNumber);
        currentDate.add(1, 'week');
    }
    return weekNumberArray;
};


/**
    * @name isISOWeekend
    * @description method to check if current date is weekend
    * @param {moment} for_date
    * 
    * @returns {Boolean} isWeekend
*/
export const isISOWeekend = (for_date) => {
    if (!for_date || !moment(for_date).isValid()) return false;
    return [0, 6].includes(moment(for_date).day());
}

/**
    * @name normalizeTimeIfMultiday
    * @description breaks and time periods if multiday, if not just returs the hours in array
    * @param {Object} t //{t.s, t.e} 
    * 
    * @returns {Array} [...t]
*/
export const normalizeTimeIfMultiday = (t) => t.s > t.e ? [{ s: t.s, e: 1440 }, { s: 0, e: t.e }] : [t];


/**
    * @name checkIfTwoTimePeriodIntersects
    * @description check if two time periods intersects, if yes return the duration. 
    * @param {Object} t1 //{t1.s, t1.e}
    * @param {Object} t2 //{t2.s, t2.e}
    * 
    * @returns {Number} duration
*/
export const checkIfTwoTimePeriodIntersects = (t1, t2) => {
    if (isNaN(t1.s) || isNaN(t1.e) || isNaN(t2.s) || isNaN(t2.e)) {
        throw new Error("Invalid params");
    }

    let duration = 0;
    const periods1 = normalizeTimeIfMultiday(t1);
    const periods2 = normalizeTimeIfMultiday(t2);
    for (let p1 of periods1) {
        for (let p2 of periods2) {
            let start = Math.max(p1.s, p2.s);
            let end = Math.min(p1.e, p2.e);
            if (start < end) {
                duration += end - start;
            }
        }
    }
    return duration;
}

/**
    * @name transformHolidayData
    * @description Formate holiday data.
    * @param {Object} holidays
    * @param {Object} fromRecommendedHoliday
    * 
    * @returns {Object} transformed holiday
*/
export const transformHolidayData = (holidays, fromRecommendedHoliday = false) => {
    return holidays.map((holiday, index) => {
        const dateObj = holiday.date?.datetime || {};
        const formattedDate = `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`;

        return {
            id: index,
            name: holiday.name,
            date: formattedDate,
            type: holiday.type || '',
            custom_name: holiday.custom_name || holiday.name,
            isSelected: holiday.isSelected || false,
            urlid: holiday.urlid || null,
            locations: holiday.locations || null,
            iso_date: holiday.date?.iso || null,

            ...(fromRecommendedHoliday && {
                additionalPayment: holiday.additionalPayment || null,
                accountedInVacation: holiday.accountedInVacation || false,
                accountedInAbsence: holiday.accountedInAbsence || false,
            })
        };
    });
};
/**
    * @name checkIfEmployeeIsAssigedForAutoUrgentCall
    * @description checks if employee has active urgent call assigned to him for automatic entries
    * @param {string} employeeExtraPayments
    * @param {Array} clientExtraPayments
    * 
    * @returns {Number} duration
*/
export const checkIfEmployeeIsAssigedForAutoUrgentCall = (employeeExtraPayments, clientExtraPayments) => {
    if (!employeeExtraPayments || !clientExtraPayments || !clientExtraPayments.length) return false;
    const employeeUrgentCallRules = employeeExtraPayments.split(',').filter(e => e.split('_')[0] === 'urgent');
    if (!employeeUrgentCallRules.length) return false;
    return clientExtraPayments.some(e => employeeUrgentCallRules.includes(e.code) && e.task && e.isActive);
}


/**
     * @name getShiftTotalTimeBetweenStartAndEnd
     * @description Calculates the total duration between a start and end time in minutes.
     * @param {number} start_time - The start time in minutes.
     * @param {number} end_time - The end time in minutes.
     * 
     * @return {number} The total duration in minutes.
 */
export const getShiftTotalTimeBetweenStartAndEnd = (start_time, end_time) => {
    const _start_time = parseInt(start_time);
    const _end_time = parseInt(end_time);
    if (_start_time > _end_time) return 1440 - _start_time + _end_time;
    else return _end_time - _start_time;
}

/**
     * @name getTimeBankTotalsFromData
     * @description Calculates the total timebank mins.
     * @param {Object} data_array - data of all the timelogs.
     * 
     * @return {number} The total minutes of timebank.
 */
export const getTimeBankTotalsFromData = (data_array) => {
    let totalAddedMinutes = 0;
    for (let i = 0; i < data_array.length; i++) {
        if (data_array[i]?.timeBank && data_array[i].timeBank.length) {
            totalAddedMinutes += data_array[i].timeBank.reduce((acc, curr) => acc + (curr.originalHours || 0), 0);
        }
    }
    return totalAddedMinutes;
}

/**
     * @name getTimebankNormalHours
     * @description Calculates the total timebank mins for regular flexitime.
     * @param {Object} data_array - data of all the timelogs.
     * 
     * @return {object} totals - The total minutes of timebank.
     * @return {number} totals.totalNormalMinsPositive - Sum of only positive minutes.
     * @return {number} totals.totalNormalMinsAll - Sum of all minutes (positive and negative).
 */
export const getTimebankNormalHours = (data_array = []) =>
    data_array.reduce(
        (totals, item) => {
            const timebank = item?.timeBank?.find(ele => ele.type === 'normal');
            const minutes = Number(timebank?.originalHours) || 0;
            totals.totalNormalMinsPositive += Math.max(0, minutes);
            totals.totalNormalMinsAll += minutes;
            return totals;
        },
        { totalNormalMinsPositive: 0, totalNormalMinsAll: 0 }
    );

/**
     * @name getOvertimeTypeArray
     * @description Returns overtime types array based on client config.
     * @param {Object} clientOvertimeTypes - client overtime types.
     * @param {Boolean} hasCompanyShifts - does client have company shifts.
     * @param {Boolean} isAutomaticMode - doses client have automatic mode.
     * @param {Object} overtimeCalcRules - client automatic overtime rules.
     * 
     * @return {Array} Array of overtime types.
 */
export const getOvertimeTypeArray = (clientOvertimeTypes, hasCompanyShifts, isAutomaticMode, overtimeCalcRules) => {
    try {
        const overtimeTypes = [];
        if (!clientOvertimeTypes?.length && !isAutomaticMode) return overtimeTypes;
        if (hasCompanyShifts) {
            clientOvertimeTypes.forEach(e => {
                const ot = new OvertimeType(e.overtime, e.overtime, e.name, e.integrated_wagetype, e.hr_int_wagetype);
                overtimeTypes.push(ot.getOvertimeObject());
            });
        } else if (isAutomaticMode) {
            const multiplier = overtimeCalcRules?.calc_percent && !isNaN(overtimeCalcRules.calc_percent) ? parseFloat(overtimeCalcRules.calc_percent) : 0;
            if (!multiplier) return overtimeTypes;
            const code = `automatic_${multiplier}`;
            const ot = new OvertimeType(code, multiplier, `Overtime ${multiplier}%`, overtimeCalcRules.integrated_wageType, overtimeCalcRules.hourlyIntegratedWageType);
            overtimeTypes.push(ot.getOvertimeObject());
        } else {
            clientOvertimeTypes.forEach(e => {
                const ot = new OvertimeType(e.code, parseFloat(e.multiplier), e.name, e.payrollWageType, e.hourlyPayrollWageType);
                overtimeTypes.push(ot.getOvertimeObject());
            });
        }
        overtimeTypes.sort((a, b) => a.value - b.value);
        return overtimeTypes;

    } catch (e) {
        console.error(e);
        return [];
    }
}

/**
     * @name getOvertimeObject
     * @description Returns creates and returns the overtime object with for changes on overtime types array based on client config.
     * @param {Object} overtimeObject - initial or current overtime object.
     * @param {Object} overtime - current editable overtime.
     * @param {Number} valueInMins - hours in minutes.
     * @param {String} valueInTimeInput - hours in time input.
     * 
     * @return {Object} Updated overtime object.
 */
export const getOvertimeObject = (overtimeObject = {}, overtime, valueInMins, valueInTimeInput) => {
    try {
        if (isNaN(valueInMins)) return overtimeObject;
        if (valueInMins < 1 && overtimeObject && overtimeObject[overtime.code] !== undefined) {
            delete overtimeObject[overtime.code];
        } else {
            const _ot = new OvertimeData(overtime.code,
                overtime.value,
                overtime.name,
                valueInTimeInput,
                valueInMins,
                overtime.payrollWageType,
                overtime.hourlyPayrollWageType);
            overtimeObject[_ot.code] = _ot.getOvertimeObject();
        }
        return overtimeObject;
    } catch (e) {
        console.error(e);
        return overtimeObject;
    }
}

/**
     * @name mergeExtraOvertimeTypes
     * @description For company-shift clients, the visible overtime-type list is normally scoped to whatever
     * overtime types are configured on the timelog's assigned company shift. But other flows (urgent work,
     * weekly overtime) can add an overtime code to a timelog that isn't part of that shift's configured list -
     * without this, that entry becomes invisible in the timesheet/approval UI even though it's real data.
     * This adds an entry for any code that's already present in one of `dataObjects` (e.g. data.overtimes,
     * data.weekly_overtimes) but missing from `overtimeTypeArray`, resolving its full definition from
     * `clientOvertimes` (the flat, always-complete Overtime_Settings list) when possible, else from the
     * data itself. Never adds a code that has no existing data - this only surfaces overtime the system
     * already recorded, not every possible configured type.
     * @param {Array} overtimeTypeArray - base overtime type list (e.g. from getOvertimeTypeArray).
     * @param {Array} dataObjects - one or more {code: {...}} maps to scan for already-recorded overtime codes.
     * @param {Array} clientOvertimes - flat Overtime_Settings list (commonStore.config.overtimes) for full metadata lookup.
     *
     * @return {Array} the merged overtime type array.
 */
export const mergeExtraOvertimeTypes = (overtimeTypeArray = [], dataObjects = [], clientOvertimes = []) => {
    const merged = [...overtimeTypeArray];
    const existingCodes = new Set(merged.map(o => `${o.code}`));
    dataObjects.forEach(dataObject => {
        Object.keys(dataObject || {}).forEach(code => {
            if (existingCodes.has(`${code}`)) return;
            const source = dataObject[code];
            const fallback = clientOvertimes?.find(o => `${o.overtime}` === `${code}`);
            merged.push({
                code: `${code}`,
                value: fallback?.overtime ?? source?.multiplier ?? code,
                multiplier: fallback?.overtime ?? source?.multiplier ?? code,
                name: fallback?.name ?? source?.name ?? code,
                payrollWageType: fallback?.integrated_wagetype ?? source?.payrollWageType ?? null,
                hourlyPayrollWageType: fallback?.hr_int_wagetype ?? source?.hourlyPayrollWageType ?? null
            });
            existingCodes.add(`${code}`);
        });
    });
    return merged;
}

/**
     * @name ratioToPercent
     * @description Returns Percent value from ratio value.
     * @param {Number} ratio - ratio value.
     * 
     * @return {Number} Percent value.
 */
export const ratioToPercent = (ratio) => {
    try {
        const diff = ratio - 1;
        return Number((diff * 100).toFixed(1));
    } catch (e) {
        console.error(e);
        return 0;
    }
}

/**
     * @name getDatesFromDurationByWeekDay
     * @description Returns Date array with matches weekday from duration.
     * @param {String} startDate - duration start.
     * @param {String} endDate - duration end.
     * @param {String} weekday - day.
     * @param {Object} weekdayMap - Object of day containing day key.
     * 
     * @return {Array} date array.
 */
export const getDatesFromDurationByWeekDay = (startDate, endDate, weekday, weekdayMap) => {
    const result = [];
    let current = moment(startDate).isoWeekday(weekdayMap[weekday]);

    if (current.isBefore(startDate)) {
        current.add(7, 'days');
    }

    while (current.isSameOrBefore(endDate)) {
        result.push(current.format('YYYY-MM-DD'));
        current.add(7, 'days');
    }

    return result;
}

/**
     * @name getWeekDays
     * @description Returns Days array with matches weekstart and weekend
     * @param {String} weekStart - week start date.
     * @param {String} weekEnd - week end date.
     * 
     * @return {Array} days array.
 */
export const getWeekDays = (weekStart, weekEnd, format = 'YYYY-MM-DD') => {
    const days = [];
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    let current = moment(weekStart);
    const end = moment(weekEnd);

    while (current.isSameOrBefore(end)) {
        const dayIndex = current.isoWeekday() - 1;
        days.push({
            key: dayNames[dayIndex],
            dayName: current.format('dddd'),
            date: current.format(format)
        });
        current = current.add(1, 'days');
    }
    return days;
}

/**
     * @name removeEmptyObjects
     * @description Remove the empty objects and return the object
     * @param {Object} obj 
     * 
     * @return {Object} Filtered object.
 */
export const removeEmptyObjects = (obj) => {
    const result = Object.fromEntries(
        Object.entries(obj)
            .map(([k, v]) => [k, v && typeof v === 'object' ? removeEmptyObjects(v) : v])
            .filter(([_, v]) => !(v && typeof v === 'object') || Object.keys(v).length > 0)
    );
    return result;
}

/**
     * @name calculateHoursFromTimes
     * @description Remove the empty objects and return the object
     * @param {Number} startTime 
     * @param {Number} endTime 
     * 
     * @return {Number} total hours.
 */
export const calculateHoursFromTimes = (startTime, endTime) => {
    const start = parseInt(startTime);
    const end = parseInt(endTime);
    let diff = end - start;
    if (diff < 0) {
        diff += 1440;
    }
    return Number((diff / 60).toFixed(2));
};